import type { MetricsRow } from 'app/services/metrics'
import { useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import MarketingGraph from './MarketingGraph'
import SummaryTab from 'app/components/analytics/SummaryTab'
import { PredefinedFilter } from 'app/filter.utils'
import { useUser } from 'app/utils'
import { isSMCOrg } from 'lib/isSMCOrg'
import { showSpend } from 'lib/showSpend'
import { formatPercent } from 'app/lib/utils'
import { StatsCardType } from 'types/StatsCardType'

export default function OverviewSection({
  funnelMetrics,
  compareToMetrics,
  isComparingToGoals,
  fromDate,
  toDate,
  dontCompare,
  showDisqualified,
  defaultFilters,
  platform,
  isLoading,
}: {
  funnelMetrics?: MetricsRow
  compareToMetrics?: MetricsRow
  isComparingToGoals: boolean
  fromDate: string
  toDate: string
  dontCompare: boolean
  showDisqualified: boolean
  defaultFilters?: PredefinedFilter
  platform?: string
  isLoading?: boolean
}) {
  const location = useLocation()
  const [stats, setStats] = useState<StatsCardType[]>()
  const user = useUser()
  const filterBasedShowSpend = user ? showSpend(isSMCOrg(user.orgId), location.search) : false

  const getTrend = () => {
    if (!user) return undefined
    if (dontCompare || !compareToMetrics || !funnelMetrics) return undefined
    if (isSMCOrg(user.orgId)) {
      return compareToMetrics?.budget
        ? {
            previous: compareToMetrics.budget,
            current: funnelMetrics.budget,
          }
        : undefined
    } else {
      return compareToMetrics?.facebookSpend || compareToMetrics?.googleSpend
        ? {
            previous: (compareToMetrics.facebookSpend || 0) + (compareToMetrics.googleSpend || 0),
            current: (funnelMetrics.facebookSpend || 0) + (funnelMetrics.googleSpend || 0),
          }
        : undefined
    }
  }
  useEffect(() => {
    if (!user) return
    const value = (num: number | undefined) => (num ?? 0).toLocaleString(`en-US`)
    const getSpend = (funnelMetrics: MetricsRow) => {
      if (!filterBasedShowSpend) return `-`
      if (isSMCOrg(user.orgId)) {
        return value(funnelMetrics.budget)
      }
      if (platform?.includes(`*`) || !platform) {
        return Math.round((funnelMetrics.facebookSpend || 0) + (funnelMetrics.googleSpend || 0)).toLocaleString(`en-US`)
      }
      const p = platform?.toLowerCase() || ``
      return Math.round(
        (p.includes(`facebook`) ? funnelMetrics.facebookSpend || 0 : 0) +
          (p.includes(`google`) || p.includes(`local service`) ? funnelMetrics.googleSpend || 0 : 0),
      ).toLocaleString(`en-US`)
    }
    if (isLoading) return
    if (funnelMetrics) {
      setStats([
        {
          label: isSMCOrg(user.orgId) ? `Budget` : `Spend`,
          value: filterBasedShowSpend ? `$${getSpend(funnelMetrics)}` : ``,
          valueClassName: filterBasedShowSpend ? undefined : `text-body-sm text-subtle`,
          trend: filterBasedShowSpend ? getTrend() : undefined,
          noValue: filterBasedShowSpend ? undefined : `Hidden until specific filters are applied`,
          novalueTooltip: filterBasedShowSpend ? undefined : ``,
          infoTooltip: {
            title: isSMCOrg(user.orgId) ? `Budget` : `Spend`,
            message: isSMCOrg(user.orgId)
              ? `The total marketing retainer or budget assigned to the channels within the date range.`
              : `The total spend within the date range associated with the sources you have selected.`,
          },
        },
        {
          label: `Net Production`,
          value: `$${value(funnelMetrics.production)}`,
          trend:
            !dontCompare && compareToMetrics?.production
              ? {
                  previous: compareToMetrics.production,
                  current: funnelMetrics.production,
                }
              : undefined,
          infoTooltip: {
            title: `Net Production`,
            message: `The total completed production after write offs and adjustments for contacts with a became a lead date within the date range.`,
          },
        },
        {
          label: `Scheduled Production`,
          value: `$${value(funnelMetrics.scheduledProduction)}`,
          trend:
            !dontCompare && compareToMetrics?.scheduledProduction
              ? {
                  previous: compareToMetrics.scheduledProduction,
                  current: funnelMetrics.scheduledProduction,
                }
              : undefined,
          infoTooltip: {
            title: `Scheduled Production`,
            message: `The total production scheduled in the future for contacts with a became a lead date within the date range. Once the appointments are completed, the associated transaction values will be recognized under Net Production.`,
          },
        },
        {
          label: `ROI`,
          value: filterBasedShowSpend ? `${formatPercent(funnelMetrics.ROI)}%` : `-`,
          trend: filterBasedShowSpend
            ? !dontCompare && compareToMetrics?.ROI
              ? {
                  previous: compareToMetrics.ROI,
                  current: funnelMetrics.ROI,
                }
              : undefined
            : undefined,
          noValue: filterBasedShowSpend ? undefined : `Hidden until specific filters are applied`,
          novalueTooltip: filterBasedShowSpend ? undefined : ``,
          infoTooltip: {
            title: `ROI`,
            message: `Return on Investment: The Sum of Net Production plus Scheduled Production minus ${
              isSMCOrg(user.orgId) ? `Budget` : `Spend`
            } divided by ${isSMCOrg(user.orgId) ? `Budget` : `Spend`}.`,
          },
        },
      ])
    }
  }, [funnelMetrics, compareToMetrics, filterBasedShowSpend, isLoading])

  if (!user) return null

  return (
    <div className={isLoading ? `animate-pulse opacity-60` : ``}>
      <SummaryTab
        defaultChartOpen
        graph={
          <MarketingGraph
            funnelMetrics={funnelMetrics}
            compareToMetrics={compareToMetrics}
            fromDate={fromDate}
            toDate={toDate}
            isComparingToGoals={isComparingToGoals}
            isLoading={funnelMetrics === undefined}
            dontCompare={dontCompare}
            showDisqualified={showDisqualified}
            defaultFilters={defaultFilters}
            shouldShowSpend={filterBasedShowSpend}
          />
        }
        statsCards={stats ?? []}
      />
    </div>
  )
}
